import { useEffect } from "react";
import { CATALOG_NORMALISED_EVENT } from "../api/nativeCatalog.js";
import { useQueryClient } from "@tanstack/react-query";
import { useActiveProviderSourceId } from "../epg/api.js";
import { dropDeviceCatalogCopy, dropDeviceGuideCopy, healthCycleNow, invalidateCatalogQueries, refreshCatalogNow } from "../library/syncActions.js";
import { SYNC_FREQUENCY_MS, useSyncSettings } from "../library/syncSettingsStore.js";
import { watchChannelFailures } from "../library/channelFailures.js";
import { useChannelHealthStore } from "../library/channelHealthStore.js";
import { nativeGuideAvailable, nativeGuideProgress } from "../api/nativeGuide.js";
import { guideTuning } from "../epg/guideFeeds.js";
import { useGuideMap } from "../epg/guideMapStore.js";
import { useSettings } from "../settings/settingsStore.js";

/** How often the app looks whether a guide download has ended. */
const GUIDE_DOWNLOAD_POLL_MS = 5_000;

/**
 * Section 12 background sync (technical-spec §8.1): while the app is open, a
 * simple interval is the reliable refresh mechanism (Periodic Background Sync
 * is Chromium-only and not guaranteed). Renders nothing; lives in the shell so
 * it runs on every screen without interrupting anything — refreshes go to the
 * device caches in the background, playback is untouched.
 *
 * One cycle = refresh EPG/VOD/Series into the device caches, then re-test the
 * channels hidden for failing (owner, 2026-10-21: "always test it on next
 * update"), both at the user's chosen frequency. The re-test NEVER runs on its
 * own faster cadence — it rides the same cycle, so "Off" means no re-tests
 * either.
 */
export function SyncScheduler() {
  const queryClient = useQueryClient();
  const sourceId = useActiveProviderSourceId();
  const frequency = useSyncSettings((s) => s.frequency);
  const hideFailing = useSyncSettings((s) => s.hideFailingChannels);

  // Live channels that play, or fail after every retry, feed the hide-after-three-tries rule.
  useEffect(() => watchChannelFailures(), []);

  // The switch turned off: whatever was hidden for failing comes back at once.
  useEffect(() => {
    if (!hideFailing) useChannelHealthStore.getState().releaseSystemHides();
  }, [hideFailing]);

  // Once per launch: the APK's old whole-catalog and whole-guide copies (see dropDeviceCatalogCopy).
  useEffect(() => {
    void dropDeviceCatalogCopy();
    void dropDeviceGuideCopy();
  }, []);

  // A catalog rebuilt behind the one that was answering (api/nativeCatalog.ts): the lists read from
  // the older build are read again, so a cold start after an update never has to wait for it.
  useEffect(() => {
    const again = () => invalidateCatalogQueries(queryClient);
    window.addEventListener(CATALOG_NORMALISED_EVENT, again);
    return () => window.removeEventListener(CATALOG_NORMALISED_EVENT, again);
  }, [queryClient]);

  // A guide download ended (the playlist's own guide, or an extra source's first one,
  // Phase 3 step 3): the screens ask again, so an extra source fills its channels
  // without waiting for their answers to go stale.
  useEffect(() => {
    if (!nativeGuideAvailable()) return;
    let was = false;
    let stopped = false;
    const timer = window.setInterval(() => {
      void nativeGuideProgress()
        .then((progress) => {
          if (stopped || !progress) return;
          if (was && !progress.downloading) void queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === "epg" });
          was = progress.downloading;
        })
        .catch(() => undefined);
    }, GUIDE_DOWNLOAD_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [queryClient]);

  // A pin or a shift changed (Phase 3 step 4): the screens ask again. Compared by
  // content, since any edit of the playlist (its last check, say) makes a new object.
  useEffect(() => {
    if (!nativeGuideAvailable()) return;
    let last = JSON.stringify(guideTuning(sourceId));
    const changed = () => {
      const now = JSON.stringify(guideTuning(sourceId));
      if (now === last) return;
      last = now;
      void queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === "epg" });
    };
    const stopMap = useGuideMap.subscribe(changed);
    const stopSettings = useSettings.subscribe(changed);
    return () => {
      stopMap();
      stopSettings();
    };
  }, [queryClient, sourceId]);

  useEffect(() => {
    const ms = SYNC_FREQUENCY_MS[frequency];
    // Not `ms === null`: an unrecognised stored frequency yields `undefined`,
    // which would slip through and reach setInterval(run, undefined) -- a 0ms
    // interval refetching the catalog and running health cycles in a tight
    // loop. The store normalizes on rehydrate; this is the second line of
    // defence.
    if (typeof ms !== "number" || ms <= 0) return;
    const run = () => {
      void refreshCatalogNow(sourceId).then(() => {
        invalidateCatalogQueries(queryClient);
        // After the update: the re-test reads the channel list it just wrote.
        return healthCycleNow(queryClient, sourceId);
      });
    };
    run();
    const timer = setInterval(run, ms);
    return () => clearInterval(timer);
  }, [queryClient, sourceId, frequency]);

  return null;
}
